import { admin } from './admin';
import { merchant } from './merchant';
import type { CatalogTree } from './types';

// Every user-visible string of both panels (UI architecture §10). Enum values are openapi.yaml's,
// verbatim; placeholders are `{name}` with English identifiers.
export const tr = {
  nav: {
    home: 'Ana sayfa',
    inbox: 'Gelen kutusu',
    waiting: 'Bekleyenler',
    products: 'Ürünler',
    more: 'Diğer',
    shoppers: 'Müşteriler',
    content: 'Instagram içerikleri',
    sales: 'Satışlar',
    reports: 'Raporlar',
    ai: 'Asistan',
    integrations: 'Entegrasyonlar',
    settings: 'Ayarlar',
    notifications: 'Bildirimler',
    notificationSettings: 'Bildirim ayarları',
    logout: 'Çıkış yap',
    dashboard: 'Genel bakış',
    merchants: 'İşletmeler',
    plans: 'Planlar',
    discounts: 'İndirimler',
    billing: 'Ödeme işlemleri',
    sectorPacks: 'Sektör paketleri',
    adapters: 'Adaptörler',
    aiQuality: 'Yapay zekâ kalitesi',
    system: 'Sistem',
    groups: {
      merchants: 'İşletmeler',
      commercial: 'Ticari',
      product: 'Ürün',
      system: 'Sistem',
    },
  },
  roles: {
    owner: 'İşletme sahibi',
    staff: 'Personel',
    admin: 'Yönetici',
    support: 'Destek',
  },
  // twoStep.<on|off>: User.totp_enrolled as plain text in the Users tabs (merchant screens §4.7); not a status group.
  twoStep: {
    on: 'Açık',
    off: 'Kapalı',
  },
  // status.<group>.<value>: the StatusBadge groups (merchant screens §4.7, admin screens §4.3).
  status: {
    conversation: {
      ai_answering: 'Asistan yanıtlıyor',
      waiting_for_staff: 'Personel bekliyor',
      staff_answering: 'Personel yanıtlıyor',
      waiting_for_shopper: 'Müşteriden yanıt bekleniyor',
      closed: 'Kapandı',
    },
    connection: {
      connecting: 'Bağlanıyor',
      active: 'Bağlı',
      degraded: 'Sorunlu',
      error: 'Hata',
      disconnected: 'Bağlı değil',
    },
    attribution: {
      confirmed: 'Kesin',
      likely: 'Olası',
    },
    freshness: {
      live: 'Canlı',
      fresh: 'Güncel',
      stale: 'Eski',
    },
    // An open window shows the remaining time instead of a label.
    window: {
      closing: 'Süre dolmak üzere',
      closed: 'Süre doldu',
    },
    // A merchant_user author shows the user's name instead of a label.
    author: {
      shopper: 'Müşteri',
      ai: 'Asistan',
      merchant_app: 'Telefondan',
      system: 'Sistem',
    },
    linkStatus: {
      auto_linked: 'Otomatik bağlandı',
      needs_review: 'İnceleme bekliyor',
      not_product: 'Ürün değil',
      confirmed: 'Onaylandı',
    },
    dataQuality: {
      missing_price: 'Fiyat eksik',
      missing_stock: 'Stok bilgisi eksik',
      missing_image: 'Görsel eksik',
      missing_attribute: 'Ayar veya gram bilgisi eksik',
    },
    // MerchantStatus and SubscriptionStatus share one group.
    merchantStatus: {
      onboarding: 'Kurulumda',
      trial: 'Deneme',
      trialing: 'Deneme',
      active: 'Aktif',
      past_due: 'Ödeme gecikmiş',
      suspended: 'Askıda',
      cancelled: 'İptal edildi',
    },
    adapterHealth: {
      ok: 'Sağlıklı',
      warning: 'Uyarı',
      critical: 'Kritik',
      unknown: 'Bilinmiyor',
    },
    jobState: {
      scheduled: 'Zamanlanmış',
      available: 'Bekliyor',
      running: 'Çalışıyor',
      retryable: 'Yeniden denenecek',
      done: 'Tamamlandı',
      cancelled: 'İptal',
      dead: 'Başarısız',
    },
    // The delivery status plus its signature check (admin screens §3.10).
    delivery: {
      received: 'Alındı',
      processed: 'İşlendi',
      failed: 'Başarısız',
      ignored: 'Yok sayıldı',
      verification_ok: 'Doğrulandı',
      verification_failed: 'Doğrulanamadı',
    },
    alert: {
      open: 'Açık',
      acknowledged: 'Görüldü',
    },
    // regression: a done run that regressed (admin screens §3.9, §4.3).
    evaluationRun: {
      queued: 'Sırada',
      running: 'Çalışıyor',
      done: 'Tamamlandı',
      regression: 'Gerileme',
      failed: 'Başarısız',
    },
    invoice: {
      pending: 'Bekliyor',
      issued: 'Düzenlendi',
      cancelled: 'İptal edildi',
      error: 'Hatalı',
    },
    paymentResult: {
      pending: 'Bekliyor',
      succeeded: 'Başarılı',
      failed: 'Başarısız',
    },
    syncRun: {
      running: 'Sürüyor',
      completed: 'Tamamlandı',
      failed: 'Başarısız',
      cancelled: 'İptal edildi',
    },
    packVersion: {
      draft: 'Taslak',
      published: 'Yayında',
    },
  },
  handoffReason: {
    complaint: 'Şikayet',
    discount_request: 'İndirim talebi',
    custom_order: 'Özel sipariş',
    payment_or_receipt: 'Ödeme / dekont',
    buyback_or_uncatalogued_price: 'Bozdurma / katalogda olmayan fiyat',
    photo_no_match: 'Eşleşmeyen fotoğraf',
    voice_note: 'Sesli mesaj',
    asked_for_person: 'Yetkili istedi',
    ai_unsure: 'Asistan emin değil',
    order_details_collected: 'Sipariş bilgileri alındı',
    stale_price: 'Fiyat güncel değil',
    policy_topic: 'Personele ayrılan konu',
    out_of_scope: 'Kapsam dışı',
  },
  errors: {
    // One message per error type of the openapi Error envelope; placeholders are the `path`s of
    // the error's `details`.
    type: {
      validation_failed: 'Bazı alanlar geçersiz. Lütfen kontrol edin.',
      invalid_request: 'İstek okunamadı. Lütfen tekrar deneyin.',
      unauthenticated: 'Oturumunuz sona erdi. Lütfen yeniden giriş yapın.',
      invalid_credentials: 'E-posta veya şifre hatalı.',
      invalid_code: 'Kod geçersiz. Tekrar deneyin.',
      ticket_expired: 'Süre doldu. Baştan giriş yapın.',
      forbidden: 'Bu işlem için yetkiniz yok.',
      csrf_rejected: 'İstek güvenlik kontrolünden geçemedi. Sayfayı yenileyip tekrar deneyin.',
      account_closed: 'Bu hesap kapatılmış.',
      email_not_verified: 'Devam etmek için e-posta adresinizi doğrulayın.',
      not_found: 'Bu öğe artık yok.',
      conflict: 'Bu kayıt siz işlem yaparken değişti; güncel hâli yüklendi.',
      idempotency_mismatch:
        'Bu istek daha önce farklı bilgilerle gönderilmiş. Sayfayı yenileyip tekrar deneyin.',
      email_taken: 'Bu e-posta adresiyle zaten bir hesap var.',
      store_already_connected: 'Zaten bağlı bir mağazanız var.',
      subscription_exists: 'Aboneliğiniz zaten aktif.',
      stale: 'Bu taslak artık geçerli değil; konuşma değişti.',
      handoff_already_claimed: 'Bu konuşmayı başka bir personel sahiplendi.',
      handoff_not_open: 'Bu aktarma artık açık değil.',
      attribution_not_active: 'Bu satış bağlantısı artık geçerli değil.',
      last_admin: 'Son yönetici devre dışı bırakılamaz veya rolü düşürülemez.',
      invalid_state: 'Bu işlem şu anki durumda yapılamaz.',
      in_use: 'Kullanımda olduğu için silinemez; pasif hale getirebilirsiniz.',
      job_not_dead: 'Bu iş artık ölü değil.',
      delivery_not_replayable:
        'Bu teslimat yeniden işlenemez: imzası doğrulanmadı ya da başarısız durumda değil.',
      payload_too_large: 'Dosya çok büyük. En fazla 5 MB yükleyebilirsiniz.',
      unsupported_media_type: 'Yalnızca JPEG, PNG veya WebP yükleyebilirsiniz.',
      window_closed: '24 saatlik yanıt süresi doldu.',
      conversation_closed: 'Bu konuşma kapatılmış.',
      routing_copy_only:
        'Yönlendirme yalnızca kopya: başka bir uygulama yanıtlıyor, ESNAF AI gönderemiyor.',
      connection_not_active: 'Bağlantı etkin değil; Entegrasyonlar sayfasını kontrol edin.',
      unsupported_part: 'Bu içerik bu kanaldan gönderilemiyor.',
      always_staff: 'Bu müşteriye asistan yanıt vermiyor; konuşma personelde kalır.',
      mode_off: 'Asistan bu kanalda kapalı veya duraklatılmış.',
      test_harness_required: 'Deneme sohbeti bu ortamda kullanılamıyor.',
      unsupported_for_connection: 'Bu işlem bu bağlantıda yapılamaz.',
      adapter_not_available: 'Bu entegrasyon hesabınız için kullanılamıyor.',
      plan_limit_reached: 'Planınızın kanal sınırına ulaştınız.',
      connection_test_failed: 'Bağlantı testi başarısız oldu.',
      plan_prerequisite_missing: 'Mağazanızda gerekli eklenti etkin değil.',
      permission_missing: 'Verilen erişim yetersiz.',
      auth_expired: 'Bağlantının yeniden yetkilendirilmesi gerekiyor.',
      billing_blocked: 'Meta hesabınızda ödeme yöntemi yok; ücretsiz mesaj hakkı doldu.',
      media_unavailable: 'Görsel alınamadı.',
      seat_limit_reached:
        'Planınızın koltukları dolu. Bir kullanıcıyı kaldırın veya planınızı yükseltin.',
      last_owner: 'Son işletme sahibi kaldırılamaz veya rolü değiştirilemez.',
      self_role_change: 'Kendi rolünüzü değiştiremezsiniz.',
      subscription_active: 'Hesabı kapatmak için önce aboneliğinizi iptal edin.',
      step_not_skippable: 'Bu adım atlanamaz.',
      step_system_managed: 'Bu adım ödeme onaylanınca kendiliğinden tamamlanır.',
      plan_required: 'Plan ve ödeme adımı tamamlanmadan yayına alınamaz.',
      token_invalid: 'Bağlantının süresi dolmuş. Yeni bir bağlantı isteyin.',
      password_too_short: 'Şifre en az 10 karakter olmalı.',
      password_breached: 'Bu şifre sızdırılmış listelerde yer alıyor, başka bir şifre seçin.',
      discount_invalid: 'İndirim kodu geçersiz.',
      no_subscription: 'Aktif bir aboneliğiniz yok.',
      card_declined: 'Kart reddedildi. Başka bir kartla tekrar deneyin.',
      refund_failed: 'Sağlayıcı iadeyi reddetti ({error_code}).',
      rate_limited: 'Çok fazla deneme. Biraz sonra tekrar deneyin.',
      unavailable: 'Sağlayıcıya şu an ulaşılamıyor, lütfen tekrar deneyin.',
      provider_unavailable: 'Ödeme sağlayıcısına şu an ulaşılamıyor, lütfen tekrar deneyin.',
      internal: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
    },
    // The message of a type that came with retry_after_s.
    wait: {
      rate_limited: 'Çok fazla deneme. {n} saniye sonra tekrar deneyin.',
      unavailable: 'Sağlayıcıya şu an ulaşılamıyor. {n} saniye sonra tekrar deneyin.',
      provider_unavailable:
        'Ödeme sağlayıcısına şu an ulaşılamıyor. {n} saniye sonra tekrar deneyin.',
      conflict: 'İşleminiz hâlâ sürüyor. {n} saniye sonra tekrar deneyin.',
    },
    // Appended to any other message that came with retry_after_s.
    retryAfter: '{n} saniye sonra tekrar deneyin.',
    // The fallback for a type the panel does not know.
    status: {
      '4xx': 'İstek tamamlanamadı. Lütfen bilgileri kontrol edip tekrar deneyin.',
      '5xx': 'Bir sorun oluştu. Lütfen tekrar deneyin.',
      network: 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.',
    },
    // The field error per validation_failed detail code; `invalid` for a code the panel does not
    // know.
    validation: {
      required: 'Bu alan zorunlu.',
      invalid_format: 'Biçim geçersiz.',
      out_of_range: 'Değer izin verilen aralığın dışında.',
      unknown_value: 'Geçersiz bir seçim.',
      too_long: 'Çok uzun.',
      unknown_placeholder: 'Tanınmayan bir yer tutucu var.',
      not_allowed: 'Bu değere izin verilmiyor.',
      invalid: 'Geçersiz değer.',
    },
    forbiddenPage: 'Bu sayfa için yetkiniz yok.',
    attemptsLeft: 'Kalan deneme: {n}',
  },
  dates: {
    relative: {
      now: 'az önce',
      minutesAgo: '{n} dk önce',
      hoursAgo: '{n} sa önce',
      yesterday: 'dün {time}',
    },
    duration: {
      seconds: '{n} sn',
      minutes: '{n} dk',
      hours: '{n} sa',
      days: '{n} gün',
    },
  },
  mock: {
    // The button labels are the English names merchant screens §5.2 gives them.
    checkout: {
      title: 'Deneme ödeme sayfası',
      succeed: 'Succeed',
      decline: 'Decline',
    },
    connect: {
      title: 'Deneme bağlantı sayfası',
      approve: 'Approve',
      deny: 'Deny',
    },
  },
  // components.<ComponentName>.*: the texts the /ui component named in the key renders.
  components: {
    AppShell: {
      sidebarLabel: 'Ana menü',
      bottomNavLabel: 'Alt menü',
      userMenu: 'Kullanıcı menüsü',
    },
    NavItem: {
      badge: '{count} yeni',
    },
    PageHeader: {
      back: 'Geri',
    },
    Breadcrumbs: {
      label: 'Sayfa yolu',
    },
    Wordmark: {
      text: 'ESNAF AI',
    },
    DataTable: {
      columns: 'Sütunlar',
      rowActions: 'İşlemler',
      expand: 'Ayrıntıları göster',
      collapse: 'Ayrıntıları gizle',
      loadMore: 'Daha fazla',
    },
    List: {
      loadMore: 'Daha fazla',
    },
    ListItem: {
      unread: 'Okunmadı',
    },
    Stat: {
      unavailable: 'Şu an alınamıyor',
      delta: {
        up: 'Artış',
        down: 'Düşüş',
        flat: 'Değişim yok',
      },
    },
    Avatar: {
      channel: {
        whatsapp: 'WhatsApp',
        instagram: 'Instagram',
      },
    },
    Timeline: {
      kind: {
        handed_off: 'Personele aktarıldı',
        paused: 'Asistan duraklatıldı',
        resumed: 'Asistan yeniden devraldı',
        assigned: 'Atandı',
        attributed: 'Satış bağlandı',
        closed: 'Konuşma kapatıldı',
        reopened: 'Konuşma yeniden açıldı',
        note: 'Not',
        message_cancelled: 'Gönderim iptal edildi',
      },
      viewSale: 'Satışı gör',
      audit: {
        reason: 'Gerekçe: {reason}',
        target: 'Hedef: {target}',
        supportSession: 'Destek oturumu: {id}',
        trace: 'İz kimliği: {id}',
      },
      loadMore: 'Daha fazla',
    },
    Thread: {
      label: 'Mesajlar',
      newMessage: 'Yeni mesaj',
      loadOlder: 'Daha eski mesajlar',
      empty: 'Henüz mesaj yok.',
      historical: 'Geçmiş sohbet, içe aktarıldı',
      observeOnly: 'Başka bir uygulama yanıtlıyor',
      edited: 'Düzenlendi',
      deleted: 'Silindi',
      staffFallback: 'Personel',
      status: {
        sending: 'Gönderiliyor',
        sent: 'Gönderildi',
        delivered: 'İletildi',
        read: 'Okundu',
        failed: 'İletilemedi',
        cancelled: 'İptal edildi',
      },
      error: {
        window_closed: '24 saatlik yanıt süresi doldu.',
        owner_changed: 'Konuşmanın sahibi değişmişti.',
        mode_off: 'Asistan bu kanalda kapalıydı.',
        routing_copy_only: 'Bu numara yalnızca kopya alıyor.',
        connection_not_active: 'Kanal bağlantısı sorunluydu.',
        recipient_unavailable: 'Müşteriye ulaşılamıyor.',
        billing_blocked: 'Meta hesabınızda ödeme yöntemi yok.',
        media_unavailable: 'Medya gönderilemedi.',
        unavailable: 'Platforma şu an ulaşılamıyor.',
        unknown: 'Mesaj iletilemedi.',
      },
      reference: {
        story_reply: 'Hikâyeye yanıt',
        ad_referral: 'Reklamdan geldi',
        post_share: 'Gönderi paylaştı',
        reel_share: 'Reels paylaştı',
        product_url: 'Ürün bağlantısı',
        quoted_reply: 'Yanıtladı',
        story_mention: 'Hikâyede bahsetti',
        pickProduct: 'Ürünü seç',
        mentionPlaceholder: 'Hikâye görseli saklanmaz.',
        mediaUnavailable: 'Görsel alınamadı',
      },
      part: {
        image: 'Fotoğraf',
        audio: 'Ses kaydı',
        video: 'Video',
        document: 'Belge',
        sticker: 'Çıkartma',
        location: 'Konum',
        contact: 'Kişi',
        unsupported: 'Desteklenmeyen mesaj',
        mediaPending: 'Medya henüz alınmadı',
        download: 'İndir: {name}',
      },
    },
    ProductCard: {
      karat: '{karat} ayar',
      grams: '{grams} g',
      similar: 'Benzer model',
      noImage: 'Görsel yok',
      stock: {
        in_stock: 'Stokta',
        limited: 'Sınırlı stok',
        out_of_stock: 'Stokta yok',
      },
      stockQuantity: '{count} adet',
    },
    PriceText: {
      missing: 'Fiyat yok',
      confirmedAt: 'onaylandı: {time}',
      perGram: '{grams} g × {rate}',
      details: 'Fiyat ayrıntısı',
    },
    ContentTile: {
      kind: {
        post: 'Gönderi',
        reel: 'Reels',
        story: 'Hikâye',
        ad: 'Reklam',
      },
      noImage: 'Görsel yok',
      mediaUnavailable: 'Görsel alınamadı (bağlantı süresi dolmuştu)',
      expired: 'Süresi doldu',
      noProducts: 'Ürün bağlı değil',
      askCount: '{count} müşteri sordu',
      shownPrice: {
        valid: 'Geçerli',
        invalid: 'Kampanya bitti',
        unmarked: 'İşaretlenmedi',
      },
    },
    Select: {
      placeholder: 'Seçin',
    },
    Combobox: {
      placeholder: 'Seçin',
      search: 'Ara',
      empty: 'Sonuç yok.',
      selectedCount: '{count} seçili',
      // The name of the list of options; cmdk's own is English.
      results: 'Sonuçlar',
    },
    DateRangePicker: {
      label: 'Dönem',
      preset: {
        today: 'Bugün',
        '7d': 'Son 7 gün',
        '30d': 'Son 30 gün',
        this_month: 'Bu ay',
        '90d': 'Son 90 gün',
        custom: 'Özel',
      },
      from: 'Başlangıç',
      to: 'Bitiş',
    },
    FileUpload: {
      label: 'Fotoğraf ekle',
    },
    CameraCapture: {
      label: 'Fotoğraf çek',
    },
    Toast: {
      // Radix announces this before every toast.
      label: 'Bildirim',
      region: 'Bilgi mesajları',
      close: 'Kapat',
      traceCode: 'Hata kodu',
      copyTrace: 'Hata kodunu kopyala',
      copied: 'Kopyalandı',
    },
    Alert: {
      dismiss: 'Kapat',
    },
    ErrorState: {
      retry: 'Tekrar dene',
      message: 'Bir sorun oluştu.',
    },
    Skeleton: {
      loading: 'Yükleniyor…',
    },
    ConfirmDialog: {
      cancel: 'Vazgeç',
    },
    Dialog: {
      close: 'Kapat',
    },
    Sheet: {
      close: 'Kapat',
      discard: {
        title: 'Değişiklikler kaydedilmedi',
        description: 'Kapatırsanız girdiğiniz bilgiler kaybolur.',
        confirm: 'Değişiklikleri at',
      },
    },
    Chart: {
      empty: 'Bu dönemde veri yok.',
    },
    FunnelBars: {
      step: {
        conversations: 'Konuşma',
        product_shared: 'Ürün paylaşıldı',
        link_opened: 'Bağlantı açıldı',
        sales: 'Satış',
      },
      unavailable: 'Şu an alınamıyor',
    },
    Composer: {
      label: 'Mesaj',
      placeholder: 'Mesajınızı yazın',
      send: 'Gönder',
      addProduct: 'Ürün kartı ekle',
      productSearch: 'Ürün ara',
      productEmpty: 'Ürün bulunamadı.',
      productError: 'Ürünler yüklenemedi.',
      removeAttachment: 'Kaldır',
      uploadFailed: 'Yüklenemedi',
      variant: 'Varyant',
      humanAgent: 'İnsan temsilci süresi: {remaining}',
      closed: 'Bu konuşma kapatıldı; müşteri yazarsa yeniden açılır.',
      reopen: 'Yeniden aç',
      disabled: {
        window_closed_whatsapp:
          '24 saatlik yanıt süresi doldu. WhatsApp Business uygulamasından yanıtlayabilirsiniz.',
        window_closed_instagram: '7 günlük yanıt süresi doldu.',
        routing_copy_only: "Bu numara yalnızca kopya alıyor; ESNAF AI'dan gönderim yapılamıyor.",
        connection_not_active: "Kanal bağlantısı sorunlu. Entegrasyonlar'ı kontrol edin.",
        offline: 'Çevrimdışısınız. Bağlantı gelince devam eder.',
        draft_pending:
          'Asistan taslağı onay bekliyor; önce taslağı gönderin, düzenleyin veya silin.',
        turn_not_open: 'Asistanın yanıtı bekleniyor; yanıt gelince yazabilirsiniz.',
      },
    },
    DraftCard: {
      title: 'Asistan taslağı',
      send: 'Gönder',
      edit: 'Düzenle',
      discard: 'Sil',
    },
    CountdownWindow: {
      remaining: 'Kalan süre: {remaining}',
      closing: 'Süre dolmak üzere: {remaining}',
      closed: 'Süre doldu',
      humanAgent: 'İnsan temsilci: {remaining}',
    },
    NotificationBell: {
      label: 'Bildirimler',
      unread: '{count} okunmamış bildirim',
      markAllRead: 'Tümünü okundu işaretle',
      viewAll: 'Tümünü gör',
      empty: 'Bildirim yok.',
      error: 'Bildirimler yüklenemedi.',
    },
  },
  merchant,
  admin,
} as const satisfies CatalogTree;
